/** Trace → macro. Only movement in the same time bucket AND button state is grouped. */
import { CFG_MAX_STEPS, CFG_MIN_STEP_MS } from '../../protocol/constants.ts';
import type { JigStep } from '../../protocol/types.ts';

export const MAX_DELTA = 127;

export interface TraceSample {
  /** Milliseconds since capture started; the final sample includes the stop time. */
  t: number;
  dx: number;
  dy: number;
  /** DOM buttons uses the same left/right/middle bit mask as JigStep. */
  buttons?: number;
}

export interface ResampleOptions {
  /** Omit for original timing. Otherwise scale the whole trace, including holds. */
  loopMs?: number;
  maxSteps?: number;
  mergeStill?: boolean;
  minStepMs?: number;
}

export interface ResampleResult {
  steps: JigStep[];
  traceMs: number;
  tracePathPx: number;
  /** Capacity failure: steps is EMPTY, never a playable partial drag/click. */
  truncated: boolean;
  requiredSteps: number;
  playbackMs: number;
  timingAdjustmentMs: number;
  buckets: number;
  splitSteps: number;
  mergedSteps: number;
}

interface EventStep {
  t: number;
  dx: number;
  dy: number;
  buttons: number;
  boundary: boolean;
}

export function resampleTrace(
  samples: readonly TraceSample[],
  options: ResampleOptions = {},
): ResampleResult {
  const maxSteps = options.maxSteps ?? CFG_MAX_STEPS;
  const minStepMs = options.minStepMs ?? CFG_MIN_STEP_MS;
  if (!Number.isInteger(maxSteps) || maxSteps < 1 || maxSteps > CFG_MAX_STEPS ||
      !Number.isInteger(minStepMs) || minStepMs < CFG_MIN_STEP_MS || minStepMs > 0xffff ||
      (options.loopMs !== undefined && (!Number.isFinite(options.loopMs) || options.loopMs <= 0))) {
    throw new RangeError('Invalid recording playback limits.');
  }

  let traceMs = 0;
  let tracePathPx = 0;
  let transitions = 0;
  let movementSamples = 0;
  let buttons = 0;
  for (const s of samples) {
    if (!Number.isFinite(s.t) || s.t < traceMs || !Number.isInteger(s.dx) ||
        !Number.isInteger(s.dy) || !Number.isInteger(s.buttons ?? 0) ||
        (s.buttons ?? 0) < 0 || (s.buttons ?? 0) > 7) {
      throw new RangeError('Invalid or out-of-order recording sample.');
    }
    traceMs = s.t;
    tracePathPx += Math.abs(s.dx) + Math.abs(s.dy);
    if (s.dx !== 0 || s.dy !== 0) movementSamples++;
    if ((s.buttons ?? 0) !== buttons) transitions++;
    buttons = s.buttons ?? 0;
  }

  const requestedMs = options.loopMs ?? traceMs;
  const scale = traceMs > 0 ? requestedMs / traceMs : 1;
  // Only compress DENSE movement. Dividing a long trace by its sample count
  // merged sparse moves seconds apart, even cancelling an out-and-back entirely.
  // Reserve room for boundaries, an initial pause and the final release.
  const movementBudget = Math.max(1, maxSteps - transitions - 3);
  const buckets = Math.max(1, Math.min(movementSamples, movementBudget));
  const bucketMs = movementSamples > movementBudget ? Math.max(minStepMs, traceMs / buckets) : 0;
  const events: EventStep[] = [];
  buttons = 0;
  let mergedSteps = 0;
  for (const s of samples) {
    const nextButtons = s.buttons ?? 0;
    const boundary = nextButtons !== buttons;
    const t = Math.round(s.t * scale);
    if (boundary) {
      events.push({ t, dx: 0, dy: 0, buttons: nextButtons, boundary: true });
      buttons = nextButtons;
    }
    if (s.dx === 0 && s.dy === 0) {
      if (!boundary && options.mergeStill === false) {
        events.push({ t, dx: 0, dy: 0, buttons, boundary: false });
      } else if (!boundary) {
        mergedSteps++;
      }
      continue;
    }
    const previous = events.at(-1);
    if (bucketMs > 0 && previous && !previous.boundary && previous.buttons === buttons &&
        (previous.dx !== 0 || previous.dy !== 0) &&
        Math.floor(previous.t / (bucketMs * scale)) === Math.floor(t / (bucketMs * scale))) {
      previous.dx += s.dx;
      previous.dy += s.dy;
    } else {
      events.push({ t, dx: s.dx, dy: s.dy, buttons, boundary: false });
    }
  }

  const endMs = Math.round(requestedMs);
  if (buttons !== 0) {
    events.push({ t: endMs, dx: 0, dy: 0, buttons: 0, boundary: true });
  }
  if (samples.length > 0 && (events.length === 0 || (events[0]?.t ?? 0) > 0)) {
    events.unshift({ t: 0, dx: 0, dy: 0, buttons: 0, boundary: false });
  }

  const output: JigStep[] = [];
  let splitSteps = 0;
  for (let index = 0; index < events.length; index++) {
    const event = events[index]!;
    const nextTime = events[index + 1]?.t ?? endMs;
    const parts = Math.max(1, Math.ceil(Math.max(Math.abs(event.dx), Math.abs(event.dy)) / MAX_DELTA));
    splitSteps += parts - 1;
    const dwell = Math.max(parts * minStepMs, nextTime - event.t);
    let x = 0;
    let y = 0;
    for (let part = 1; part <= parts; part++) {
      const targetX = Math.round(event.dx * part / parts);
      const targetY = Math.round(event.dy * part / parts);
      // Emit a fast stroke as fast as HID allows; keep the following pause AFTER
      // it. Spreading the split across the whole dwell turned pauses into motion.
      let delay = part < parts ? minStepMs : dwell - (parts - 1) * minStepMs;
      // Long pauses/holds need several uint16 dwells, not a saturated delay.
      const chunks = Math.ceil(delay / 0xffff);
      for (let chunk = 0; chunk < chunks; chunk++) {
        const chunkDelay = Math.max(minStepMs, Math.round(delay / (chunks - chunk)));
        output.push({
          dx: chunk === 0 ? targetX - x : 0,
          dy: chunk === 0 ? targetY - y : 0,
          wheel: 0,
          buttons: event.buttons,
          delayMs: chunkDelay,
        });
        delay -= chunkDelay;
      }
      x = targetX;
      y = targetY;
    }
  }

  const playbackMs = stepsCycleMs(output);
  const truncated = output.length > maxSteps;
  return {
    steps: truncated ? [] : output,
    traceMs, tracePathPx, truncated, requiredSteps: output.length, playbackMs,
    timingAdjustmentMs: Math.max(0, playbackMs - endMs),
    buckets, splitSteps, mergedSteps,
  };
}

export function stepsPathPx(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.abs(s.dx) + Math.abs(s.dy), 0);
}

export function stepsCycleMs(steps: readonly JigStep[]): number {
  return steps.reduce((sum, s) => sum + Math.max(s.delayMs, CFG_MIN_STEP_MS), 0);
}
